import React, { useEffect, useRef, useState, useCallback } from 'react';
import { shortenString } from 'utils/StringUtils';
import classNames from 'classnames';
// eslint-disable-next-line import/no-restricted-paths
import { TextItem } from 'domains/designers/TruthTable/components/TextItem';

export interface IntersectionEditorCommands {
  setIntersectionStringValue(intersection: IntersectionEditorInfo, value: string): void;
  showFullEditor(intersection: IntersectionEditorInfo): void;

  moveLeft?(current: IntersectionEditorInfo): boolean;
  moveUp?(current: IntersectionEditorInfo): boolean;
  moveRight?(current: IntersectionEditorInfo): boolean;
  moveDown?(current: IntersectionEditorInfo): boolean;
  moveNext?(current: IntersectionEditorInfo): boolean;
}

export interface IntersectionEditorInfo {
  rowId?: string;
  columnId?: string;

  displayValue: string;

  dataType: string;
  mappingType: string;
  /**
   * Determine can navigate to other editor by left/up/right/down/tab keys
   */
  canNavigate: boolean;
  commands?: IntersectionEditorCommands;
  initialViewType?: 'label' | 'editor';
}

export interface IInlineEditorProps {
  className?: string;
  /**
   * Text is displayed when there is no sutable inline editor for given dataType and mappingType.
   */
  text: string;
  onTextClick: (e: React.MouseEvent<HTMLSpanElement>) => void;
  intersection: IntersectionEditorInfo;
  isInDebug: boolean;
  onCreated?: (inlineEditor?: any) => void;
}

/**
 * Wrapper around MVC inline editor.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function InlineEditor({ intersection, text, isInDebug, className, onTextClick, onCreated }: IInlineEditorProps) {
  const inlineEditorRef = useRef<HTMLDivElement>(null);
  const [inlineEditorInstance, setInlineEditor] = useState<any>(null);
  const [isRendered, setRendered] = useState(false);
  const handleTextClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      !isInDebug && onTextClick(e);
    },
    [isInDebug, onTextClick]
  );

  useEffect(() => {
    // to show text item only when we are sure that there is no suitable inline editor (After calling "MatrixRule.initializeEditor").
    setRendered(true);
  }, []);

  useEffect(() => {
    if (inlineEditorRef.current != null && !isInDebug) {
      inlineEditorRef.current.innerHTML = '';
      if (!IS_TEST && !DEV_SERVER) {
        const inlineEditor = $DP.Designers.MatrixRule.initializeEditor(inlineEditorRef.current, intersection);
        onCreated?.(inlineEditor);
        setInlineEditor(inlineEditor);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intersection, isInDebug]);

  // we use this key to re-render <div class="tt-inline-editor"> when "mappingType" or "datatype" are changed
  // to remove all event listeners which were attached to it inside "initializeEditor" method
  const key = `${intersection.dataType} ${intersection.mappingType}`;
  return (
    <>
      {!inlineEditorInstance && isRendered && (
        <TextItem className={className} onClick={handleTextClick}>
          {shortenString(text, 70, { separator: '.' })}
        </TextItem>
      )}
      {!isInDebug && <div key={key} className={classNames('tt-inline-editor', className)} ref={inlineEditorRef}></div>}
    </>
  );
}
